import formatDate, { formatUtcDate } from '@/utils/formatDate';
import { getFinalSignature } from '@/utils/reportSignatures';

import { ReportLocationEnum, ReportNote } from '@/types/alert';
import { Address } from '@/types/organization';
import { Patient } from '@/types/patient';
import {
  ReportColor,
  ReportType,
  SignatureStatus,
  TransmissionReport
} from '@/types/transmissionReport';
import { User } from '@/types/user';
import { DeviceLeadLocation, VtDeviceLead, VtLeadVendor } from '@/types/vendorTransmissionReport';

export const checkIfReportIsEditedAfterSigned = (report: TransmissionReport) => {
  if (
    !report?.signatureStatus ||
    report.signatureStatus !== SignatureStatus.Signed ||
    !report.editedDate ||
    !report.editedUser
  ) {
    return false;
  }
  const latestSignature = getFinalSignature(report.signatures ?? []);
  if (!latestSignature) return false;
  const latestSignatureDate = new Date(latestSignature.updatedAt);
  const editedDate = new Date(report.editedDate);
  return editedDate > latestSignatureDate;
};

const reportColorToNameMap = (heartFailure: boolean) => {
  return {
    [ReportColor.Yellow]: heartFailure ? 'Requires Review: Heart Failure' : 'Requires Review',
    [ReportColor.Green]: heartFailure ? 'Routine: Heart Failure' : 'Routine',
    [ReportColor.Red]: heartFailure ? 'Urgent: Heart Failure' : 'Urgent',
    [ReportColor.Gray]: ''
  };
};

export const fetchTitleText = (
  heartFailure: boolean,
  reportColor: ReportColor,
  isInClinic: boolean
) => {
  const prefix = reportColorToNameMap(heartFailure)[reportColor];
  const isDefaultPrefix = ['Routine', 'Default'].includes(prefix);

  if (isInClinic) {
    return isDefaultPrefix ? 'In-clinic Report' : `${prefix}: In-clinic Report`;
  }

  return `${prefix} Report`;
};

export const getDefaultPatientValues = (patient?: Patient | null) => {
  if (!patient) return {};

  const {
    mrn2,
    birthDate,
    monitorReason,
    diagnosisOption,
    oac,
    referringPhysician,
    practitioner,
    referringProvider
  } = patient || {};

  return {
    mrn2: mrn2 ?? '-',
    birthDate: formatDate(birthDate) ?? '-',
    referringPhysician: referringPhysician ?? '',
    referringProvider: referringProvider ?? null,
    monitorReason: monitorReason ? { label: monitorReason, value: monitorReason } : undefined,
    diagnosisOption: diagnosisOption
      ? { label: diagnosisOption, value: diagnosisOption }
      : undefined,
    oac: oac ?? '-',
    practitionerId: practitioner
      ? {
          label:
            (practitioner.prefix?.length ? `${practitioner.prefix} ` : '') +
            `${practitioner.given} ${practitioner.family}`,
          value: practitioner.id
        }
      : undefined
  };
};

export const fetchReferringProvider = (data: Record<string, any>) => {
  return data?.referringProvider
    ? `${data.referringProvider.firstName} ${data.referringProvider.lastName} (${data.referringProvider.NPI})`
    : (data.referringPhysician ?? '-');
};

export const fetchPractitionerName = (user: User | undefined) => {
  return user ? `${user.given} ${user.family}` : '-';
};

export const fetchAlertNote = (notes: ReportNote[], location: ReportLocationEnum) => {
  return notes.find((note) => note.location === location)?.note ?? '';
};

export const fetchDeviceLeadData = (
  deviceLeads: VtDeviceLead[],
  leadVendorsList?: VtLeadVendor[]
) => {
  const leadDataObj: Record<DeviceLeadLocation, Partial<VtDeviceLead>> = {} as Record<
    DeviceLeadLocation,
    Partial<VtDeviceLead>
  >;

  if (deviceLeads.length > 0) {
    for (const value of deviceLeads) {
      const location = (value.leadLocation ?? '') as DeviceLeadLocation;

      const vendor = leadVendorsList?.find(
        (vendor) => parseInt(vendor.leadVendorId) === parseInt(value.vendorId || '0')
      );

      leadDataObj[location] = {
        sensing: value.sensing ?? '-',
        impedance: value.impedance ?? '-',
        threshold: value.threshold ?? '-',
        pulsewidth: value.pulsewidth ?? '-',
        vendor: {
          vendorId: value.vendorId ?? '-',
          vendorName: vendor?.leadVendorName ?? '-'
        } as any
      };
    }
  }

  return leadDataObj;
};

export function formatCodesWithLineBreaks(
  codes: Array<{ value: string }> | null,
  lineBreakAfter = 3
): string {
  if (!codes || codes?.length === 0) {
    return '-';
  }
  return codes
    .map((code, index) => {
      return `${index > 0 && index % lineBreakAfter === 0 ? '\n' : ''}${code.value}`;
    })
    .join(', ');
}

export function fetchClinicAddressLines(address: Address | undefined) {
  const { line1, line2 } = address || {};
  const addressLine1 = [line1, line2].filter(Boolean).join(', ');
  const { city, postalCode, state } = address || {};
  const addressLines2 = [city, state, postalCode].filter(Boolean).join(', ');
  const addressLines = [addressLine1, addressLines2];
  return addressLines;
}

export const displayTableValue = (
  value: string | null | undefined,
  placeholder?: '-',
  separator?: ','
) => {
  if (typeof value === 'string' && value?.length && separator && value.includes(separator)) {
    return value.split(separator, 1)[0];
  }
  return value ?? placeholder;
};

export const getReportDownloadName = (
  reportType: string,
  givenName: string,
  familyName: string,
  transmissionDate: string,
  isFlagHeartFailure: boolean
) => {
  try {
    const patientNameDateSuffix = `${givenName}_${familyName}_${formatUtcDate(transmissionDate, 'MM_DD_YYYY')}`;
    if (reportType === ReportType.InClinic) {
      return `${patientNameDateSuffix}_inclinic.pdf`;
    } else if (reportType === ReportType.Manual) {
      return `${patientNameDateSuffix}_manual.pdf`;
    } else {
      return `${patientNameDateSuffix}${isFlagHeartFailure ? '_heartfailure' : ''}.pdf`;
    }
  } catch (e) {
    return '`RhythmReport+.pdf`';
  }
};

export const chunkSubstr = (str: string, size: number) => {
  const numChunks = Math.ceil(str.length / size);
  const chunks = new Array(numChunks);

  for (let i = 0, o = 0; i < numChunks; ++i, o += size) {
    chunks[i] = str.substr(o, size);
  }

  return chunks;
};
